import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Sparkles, BookOpen, Wand2, Star, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Layout } from "@/components/Layout";
import StoryCreator from "@/components/StoryCreator";
import { Story } from "@/types/database";

const Index = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [createdStory, setCreatedStory] = useState<Story | null>(null);

  const handleStoryCreated = (story: Story) => {
    setCreatedStory(story);
    // Optionally navigate to the story view
    setTimeout(() => {
      navigate(`/stories/${story.id}`);
    }, 2000);
  };

  // If still loading, show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Sparkles className="h-8 w-8 animate-pulse text-purple-500" />
          <span className="text-lg">Loading your story world...</span>
        </div>
      </div>
    );
  }

  // If not logged in, show the return to home button
  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <Sparkles className="h-12 w-12 text-purple-500" />
            <h1 className="text-3xl font-bold text-gray-800">StoryBook</h1>
          </div>
          <h2 className="text-xl text-gray-600">Please log in to create magical stories</h2>
          <Button onClick={() => navigate("/auth")} size="lg" className="bg-purple-600 hover:bg-purple-700">
            <BookOpen className="mr-2 h-5 w-5" />
            Start Your Adventure
          </Button>
        </div>
      </div>
    );
  }

  // Show success message if story was created
  if (createdStory) {
    return (
      <Layout>
        <div className="py-6 px-6 max-w-4xl mx-auto">
          <div className="text-center space-y-6">
            <div className="flex items-center justify-center space-x-2">
              <CheckCircle className="h-12 w-12 text-green-500" />
              <h1 className="text-3xl font-bold text-gray-800">Story Created!</h1>
            </div>
            <Card className="max-w-md mx-auto">
              <CardContent className="pt-6">
                <h3 className="text-xl font-semibold mb-2">{createdStory.title}</h3>
                <p className="text-gray-600 mb-4">{createdStory.description}</p>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Genre: {createdStory.genre}</span>
                  <span>Age: {createdStory.age_group}</span>
                </div>
              </CardContent>
            </Card>
            <p className="text-gray-600">Redirecting to your story...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-6 px-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Sparkles className="h-8 w-8 text-purple-500" />
            <h1 className="text-3xl font-bold text-gray-800">Create Your Story</h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Transform your imagination into a beautiful, personalized storybook with AI-powered illustrations
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Story Creator */}
          <div className="lg:col-span-2">
            <StoryCreator onStoryCreated={handleStoryCreated} />
          </div>

          {/* Sidebar with Features */}
          <div className="space-y-6">
            {/* Features Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Star className="h-5 w-5 text-yellow-500" />
                  <span>AI Features</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Sparkles className="h-5 w-5 text-purple-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Smart Story Generation</h4>
                    <p className="text-sm text-gray-600">AI creates engaging, age-appropriate stories</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Wand2 className="h-5 w-5 text-purple-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Personalized Content</h4>
                    <p className="text-sm text-gray-600">Stories tailored to your child's age and interests</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <BookOpen className="h-5 w-5 text-purple-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Multiple Genres</h4>
                    <p className="text-sm text-gray-600">Adventure, fantasy, educational, and more</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">💡 Story Tips</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm text-gray-600">
                  <p className="font-medium mb-1">Be Specific</p>
                  <p>Include details about characters, settings, and themes</p>
                </div>
                <div className="text-sm text-gray-600">
                  <p className="font-medium mb-1">Consider Age</p>
                  <p>Choose appropriate complexity for your child's age</p>
                </div>
                <div className="text-sm text-gray-600">
                  <p className="font-medium mb-1">Add Personal Elements</p>
                  <p>Include your child's name or favorite things</p>
                </div>
              </CardContent>
            </Card>

            {/* Recent Stories */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">📚 Recent Stories</CardTitle>
              </CardHeader>
              <CardContent>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/stories')}
                >
                  View All Stories
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Index;
